import { useParams } from "react-router-dom"
import { PaymentLinkType, usePaymentLink } from "@/api/payment-links"
import { Network } from "@/config/tokens"
import InnerScreenHeader from "@/components/layout/inner-screen-header"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import CopyIcon from "@/components/icons/copy"
import QrCodeIcon from "@/components/icons/qr-code"
import { useCopyToClipboard } from "usehooks-ts"
import { toast } from "sonner"
import PaymentLinkQrModal from "./payment-link-qr.modal"
import { getShortUrl } from "@/api/payment-pages"
import {
  ViewForm,
  ViewFormColumn,
  ViewFormItem,
} from "@/components/ux/view-form"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import NetworkBadge from "@/components/ux/network.badge"
import { TokenIcon } from "@/components/token"
import PaymentsTable from "@/payments/payments-table"

export default function PaymentLinkViewScreen() {
  const { id } = useParams()
  const { paymentLink } = usePaymentLink(id)
  const [, copy] = useCopyToClipboard()

  if (!paymentLink) return null

  const handleCopy = async () => {
    await copy(getShortUrl(paymentLink.paymentPage.shortId))
    toast.success("Link copied to clipboard")
  }

  return (
    <div className="flex flex-col gap-4">
      <InnerScreenHeader
        title="Payment link"
        to="/payment-links"
        tryBackFirst={false}
      />
      <Card className="shadow-sm">
        <CardContent>
          <div className="text-xl font-semibold">{paymentLink.title}</div>
          <div className="text-muted-foreground">
            Copy and share this link to start accepting payments
          </div>
          <div className="flex flex-col md:flex-row gap-4 pt-3">
            <input
              name="shortUrl"
              readOnly
              type="text"
              value={getShortUrl(paymentLink.paymentPage.shortId)}
              className="flex-1 p-2 bg-blue-50 border-blue-100 border rounded-full text-sm text-blue-900 px-4"
            />
            <div className="flex flex-row gap-4">
              <Button
                onClick={handleCopy}
                data-testid="copy-link"
                className="flex-grow md:flex-grow-0"
              >
                <CopyIcon /> Copy
              </Button>
              <PaymentLinkQrModal
                url={getShortUrl(paymentLink.paymentPage.shortId)}
              >
                <Button variant="outline" className="flex-grow md:flex-grow-0">
                  <QrCodeIcon /> QR code
                </Button>
              </PaymentLinkQrModal>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="font-semibold">
          {paymentLink.paymentsCount} payments collected
        </CardHeader>
        <CardContent>
          <PaymentsTable paymentLinkId={paymentLink.id} infinite />
        </CardContent>
      </Card>
      <h3 className="text-lg font-semibold">Link Details</h3>
      <Card className="shadow-sm">
        <CardContent>
          <ViewForm>
            <ViewFormColumn>
              <ViewFormItem label="Status">
                <Badge variant={paymentLink.active ? "active" : "inactive"}>
                  {paymentLink.active ? "Active" : "Inactive"}
                </Badge>
              </ViewFormItem>
              <ViewFormItem label="Created on">
                {format(new Date(paymentLink.createdAt), "MMM do, yyyy")}
              </ViewFormItem>
              <ViewFormItem label="Type">
                {paymentLink.type === PaymentLinkType.CUSTOM_AMOUNT
                  ? "Custom amount"
                  : "Fixed amount"}
              </ViewFormItem>
              <ViewFormItem label="Description">
                {paymentLink.description}
              </ViewFormItem>
              <ViewFormItem label="Token">
                <div className="flex gap-1">
                  <TokenIcon name={paymentLink.token} size={20} />{" "}
                  {paymentLink.token}
                </div>
              </ViewFormItem>
              {paymentLink.amount && (
                <ViewFormItem
                  label={
                    paymentLink.type === PaymentLinkType.CUSTOM_AMOUNT
                      ? "Suggested amount"
                      : "Amount"
                  }
                >
                  {paymentLink.amount} {paymentLink.token}
                </ViewFormItem>
              )}
              <ViewFormItem label="Networks">
                <div className="flex gap-2 flex-wrap flex-shrink w-8/12">
                  {paymentLink.networks.map((network: Network, i: number) => (
                    <NetworkBadge network={network} key={i} />
                  ))}
                </div>
              </ViewFormItem>
            </ViewFormColumn>
            {paymentLink.type === PaymentLinkType.CUSTOM_AMOUNT && (
              <ViewFormColumn>
                {paymentLink.minAmount && (
                  <ViewFormItem label="Min Amount">
                    {paymentLink.minAmount} {paymentLink.token}
                  </ViewFormItem>
                )}
                {paymentLink.maxAmount && (
                  <ViewFormItem label="Max Amount">
                    {paymentLink.maxAmount} {paymentLink.token}
                  </ViewFormItem>
                )}
                {paymentLink.presetAmounts &&
                  paymentLink.presetAmounts.length > 0 && (
                    <ViewFormItem label="Preset Amounts">
                      <div className="flex gap-2 flex-wrap flex-shrink w-8/12">
                        {paymentLink.presetAmounts.map((amount: number, i: number) => (
                          <Badge variant="gray" key={i}>
                            {amount} {paymentLink.token}
                          </Badge>
                        ))}
                      </div>
                    </ViewFormItem>
                  )}
              </ViewFormColumn>
            )}
          </ViewForm>
        </CardContent>
      </Card>
    </div>
  )
}
